import { useCallback, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { TransactionProcessingDurationDto } from '@/api/Metrics/Types/TransactionProcessingMetricsDto'
import { Link } from '@/components/Link'
import { Loading } from '@/components/Loading'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { useApiClient } from '@/utils/api/ApiClient'
import { MetricsApiClient } from '@/utils/api/metricsApi'
import { useFetchOne } from '@/utils/api/useFetch'
import { formatLocalDateUTC } from '@/utils/date'
import { links } from '@/utils/featureToggles'
import { orderBy, SortDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'

import { PaymentProviderTab } from '../../PaymentProviders/PaymentProviderSlug'
import { formatDuration } from './formatDuration'
import { TransactionProcessingMetricsDetailsDialog } from './TransactionProcessingMetricsDetailsDialog'

const crumbs = [
  {
    title: 'Transaction Processing Metrics',
    link: links.administrationTransactionProcessingMetrics.url,
  },
]

export const TransactionProcessingMetricsPage = (): JSX.Element => {
  const metricsApiClient = useApiClient(MetricsApiClient)
  const fetchMetrics = useCallback(
    () => metricsApiClient.getTransactionProcessingMetrics(),
    [metricsApiClient]
  )
  const { data, isLoading } = useFetchOne(fetchMetrics)
  const [sort, setSort] = useState<SortDescriptor[]>([])
  const [selectedDuration, setSelectedDuration] = useState<TransactionProcessingDurationDto>()

  const durations = useMemo(() => orderBy(data?.durations ?? [], sort), [data?.durations, sort])

  return (
    <>
      <Breadcrumbs crumbs={crumbs} />
      <p className='mb-4 text-sm'>
        <strong>Last Calculated:</strong> {formatLocalDateUTC(data?.generatedAtUtc, true) ?? '—'}
      </p>
      <Loading isLoading={isLoading} className='min-h-40'>
        <Grid
          data={durations}
          resizable
          sort={sort}
          sortable={{ allowUnsort: true, mode: 'multiple' }}
          onSortChange={(event: GridSortChangeEvent) => setSort(event.sort)}
        >
          <Column field='type.name' title='Transaction Type' />
          <Column
            field='paymentProvider.name'
            title='Payment Provider'
            cell={(props: GridCellProps) => {
              const duration = props.dataItem as TransactionProcessingDurationDto
              const providerUrl = generatePath(
                links.administrationPaymentProvidersConfig.detailsUrl as string,
                {
                  id: duration.paymentProvider.id,
                  slug: PaymentProviderTab.OPTIONS_AND_LIMIT,
                }
              )

              return (
                <td>
                  <Link className='text-primary underline' to={providerUrl}>
                    {duration.paymentProvider.name}
                  </Link>
                </td>
              )
            }}
          />
          <Column
            field='overallMedianSeconds'
            title='Overall Median'
            cell={(props: GridCellProps) => (
              <td>{formatDuration(props.dataItem.overallMedianSeconds)}</td>
            )}
          />
          <Column field='overallSampleSize' title='Sample Size' />
          <Column
            title='Transitions'
            sortable={false}
            cell={(props: GridCellProps) => (
              <td>
                <Button
                  variant='link'
                  className='h-auto p-0'
                  onClick={() => setSelectedDuration(props.dataItem)}
                >
                  View Details
                </Button>
              </td>
            )}
          />
        </Grid>
      </Loading>
      <TransactionProcessingMetricsDetailsDialog
        duration={selectedDuration}
        onClose={() => setSelectedDuration(undefined)}
      />
    </>
  )
}
